/**
 * Hero Scene - Landing page with three deformed plane meshes
 * Manages the visual elements for the hero/landing section
 */

import * as THREE from 'three'
import AssetManager from './AssetManager.js'
import vertexShader from './shaders/vertex.glsl?raw'
import fragmentShader from './shaders/fragment.glsl?raw'

class HeroScene {    
    constructor(scene) {
        this.scene = scene
        this.assetManager = new AssetManager(scene)
        this.meshes = []
        this.materials = []
        
        // Calculate responsive layout parameters based on screen dimensions
        this.layoutParams = this.calculateResponsiveLayout()        // Default shader parameters
        this.shaderParams = {
            grungeFrequency: 10000.0,
            grungeSpeed: 0.0,
            deformXY: 1.0,
            deformZ: 1.0,
            twistStrength: 1.0,
            fadeSize: 1.0,
            centerDotSize: 0.2,
            shadowStrength: 0.0,
            alphaThreshold: 0.5,
            edgeShadingStrength: 0.05
        }

        this.resolution = new THREE.Vector2(window.innerWidth, window.innerHeight)

        this.initializeMaterials()
    }
    
    /**
     * Calculate responsive layout parameters based on screen size
     * Blends between known desktop (1920x1080) and mobile (430x932) configurations
     */
    calculateResponsiveLayout() {
        const width = window.innerWidth
        const height = window.innerHeight
        const aspectRatio = width / height
        
        // Known configurations
        const desktop = {
            width: 1920,
            height: 1080,
            aspectRatio: 1920 / 1080, // ~1.778
            params: {
                overallDistance: 10.0,
                horizontalSpread: 11.5,
                verticalSpread: 3.0,
                depthVariation: 3.0,
                scale: 2.6,
                horizontalOffset: 1.6,
                verticalOffset: -0.1
            }
        }
        
        const mobile = {
            width: 430,
            height: 932,
            aspectRatio: 430 / 932, // ~0.461
            params: {
                overallDistance: 14.0,
                horizontalSpread: 6.0,
                verticalSpread: 10.0,
                depthVariation: 3.0,
                scale: 2.6,
                horizontalOffset: 4.2,
                verticalOffset: -1.6
            }
        }
        
        // Calculate blend factor based on aspect ratio
        // 0 = mobile, 1 = desktop
        const minAspect = mobile.aspectRatio
        const maxAspect = desktop.aspectRatio
        const blendFactor = Math.max(0, Math.min(1, 
            (aspectRatio - minAspect) / (maxAspect - minAspect)
        ))
        
        // Blend between mobile and desktop values
        const lerp = (a, b, t) => a + (b - a) * t
        
        const params = {
            overallDistance: lerp(mobile.params.overallDistance, desktop.params.overallDistance, blendFactor),
            horizontalSpread: lerp(mobile.params.horizontalSpread, desktop.params.horizontalSpread, blendFactor),
            verticalSpread: lerp(mobile.params.verticalSpread, desktop.params.verticalSpread, blendFactor),
            depthVariation: lerp(mobile.params.depthVariation, desktop.params.depthVariation, blendFactor),
            scale: lerp(mobile.params.scale, desktop.params.scale, blendFactor),
            horizontalOffset: lerp(mobile.params.horizontalOffset, desktop.params.horizontalOffset, blendFactor),
            verticalOffset: lerp(mobile.params.verticalOffset, desktop.params.verticalOffset, blendFactor)
        }
        
        console.log(`Responsive Layout Calculated:`)
        console.log(`  Screen: ${width}x${height} (aspect: ${aspectRatio.toFixed(3)})`)
        console.log(`  Blend factor: ${blendFactor.toFixed(3)} (0=mobile, 1=desktop)`)
        console.log(`  Params:`, params)
        
        return params
    }
    
    /**
     * Create shader materials with different colors
     */    
    initializeMaterials() {
        // Deeper pastel colors
        const colors = [
            { name: 'red', color: new THREE.Color(0.902, 0.333, 0.106) },   // R:230 V:85 B:27 (plus orangé)
            { name: 'yellow', color: new THREE.Color(0.980, 0.871, 0.086) },   // R:250 V:222 B:22
            { name: 'blue', color: new THREE.Color(0.051, 0.106, 0.624) }      // R:13 V:27 B:159
        ]

        colors.forEach(({ name, color }) => {
            const material = new THREE.ShaderMaterial({
                vertexShader: vertexShader,
                fragmentShader: fragmentShader,
                side: THREE.DoubleSide,
                transparent: false,
                depthWrite: true,
                uniforms: {
                    uTime: { value: 0 },
                    uSunDirection: { value: new THREE.Vector3(1, 1, 1) },
                    uCenterColor: { value: color },
                    uGrungeFrequency: { value: this.shaderParams.grungeFrequency },
                    uGrungeSpeed: { value: this.shaderParams.grungeSpeed },
                    uDeformXY: { value: this.shaderParams.deformXY },
                    uDeformZ: { value: this.shaderParams.deformZ },
                    uTwistStrength: { value: this.shaderParams.twistStrength },
                    uFadeSize: { value: this.shaderParams.fadeSize },
                    uCenterDotSize: { value: this.shaderParams.centerDotSize },                    uShadowStrength: { value: this.shaderParams.shadowStrength },
                    uAlphaThreshold: { value: this.shaderParams.alphaThreshold },
                    uResolution: { value: this.resolution.clone() },
                    uDebugNormals: { value: 0.0 }, // Debug mode: 0 = normal, 1 = visualize normals
                    uEdgeShadingStrength: { value: this.shaderParams.edgeShadingStrength }
                }
            })
            
            this.materials.push({ name, material })
        })
    }

    /**
     * Load and setup all meshes for the hero scene
     */
    async load() {
        try {
            console.log('Loading Hero Scene...')
            
            return new Promise((resolve, reject) => {
                this.assetManager.assetLoader.loadAssetsFromFile(
                    '/assets/scene-models.json',
                    (loadedAssets) => {
                        console.log('Hero Scene assets loaded:', loadedAssets)
                          const plane1Asset = loadedAssets['deformed-plane-1']
                        const plane3Asset = loadedAssets['deformed-plane-3']
                          if (plane1Asset && plane1Asset.scene) {
                            // Create 6 meshes total (2 of each color) for full screen coverage
                            // Red meshes (left side)
                            this.createMesh(plane1Asset, 'red', 
                                -this.layoutParams.horizontalSpread, 
                                this.layoutParams.verticalSpread * 0.5, 
                                -this.layoutParams.overallDistance, 
                                0, 0, 0)
                            this.createMesh(plane1Asset, 'red', 
                                -this.layoutParams.horizontalSpread * 1.2, 
                                -this.layoutParams.verticalSpread * 0.3, 
                                -this.layoutParams.overallDistance - this.layoutParams.depthVariation, 
                                Math.PI * 0.3, Math.PI * 0.2, 0)
                            
                            // Yellow meshes (center)
                            this.createMesh(plane1Asset, 'yellow', 
                                0, 
                                0, 
                                -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 0.5, 
                                Math.PI * 0.5, 0, Math.PI * 0.25)
                            this.createMesh(plane3Asset, 'yellow', 
                                this.layoutParams.horizontalSpread * 0.3, 
                                -this.layoutParams.verticalSpread * 0.8, 
                                -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 0.2, 
                                Math.PI * 0.4, Math.PI * 0.1, Math.PI * 0.15)
                        }
                        
                        if (plane3Asset && plane3Asset.scene) {
                            // Blue meshes (right side)
                            this.createMesh(plane3Asset, 'blue', 
                                this.layoutParams.horizontalSpread, 
                                this.layoutParams.verticalSpread * 0.3, 
                                -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 0.7, 
                                Math.PI * 0.2, Math.PI * 0.3, 0)
                            this.createMesh(plane3Asset, 'blue', 
                                this.layoutParams.horizontalSpread * 1.3, 
                                -this.layoutParams.verticalSpread * 0.5, 
                                -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 1.5, 
                                Math.PI * 0.6, 0, Math.PI * 0.4)
                        }
                        
                        console.log(`Hero Scene loaded with ${this.meshes.length} meshes`)
                        resolve(this.meshes)
                    }
                )
            })
        } catch (error) {
            console.error('Error loading Hero Scene:', error)
            throw error
        }
    }    

    /**
     * Create a mesh with specific positioning and rotation
     * @param {object} asset - The GLTF asset
     * @param {string} colorName - Name of the material ('red', 'yellow', 'blue')
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} z - Z position (distance from camera)
     * @param {number} rotX - Rotation around X axis
     * @param {number} rotY - Rotation around Y axis
     * @param {number} rotZ - Rotation around Z axis
     */    createMesh(asset, colorName, x, y, z, rotX = 0, rotY = 0, rotZ = 0) {
        const mesh = asset.scene.clone()
        const meshGeometry = mesh.children[0]
        const material = this.materials.find(m => m.name === colorName).material
        
        meshGeometry.material = material
        
        // Position with offset applied
        meshGeometry.position.set(
            x + this.layoutParams.horizontalOffset, 
            y + this.layoutParams.verticalOffset, 
            z
        )
        
        // Rotation
        meshGeometry.rotation.set(rotX, rotY, rotZ)
        
        // Scale - further meshes appear bigger to maintain screen size
        const distanceFactor = Math.abs(z) / this.layoutParams.overallDistance
        const scale = this.layoutParams.scale * distanceFactor
        meshGeometry.scale.set(scale, scale, scale)
        
        this.scene.add(meshGeometry)
        this.meshes.push(meshGeometry)
        console.log(`Created ${colorName.toUpperCase()} mesh at (${x.toFixed(1)}, ${y.toFixed(1)}, ${z.toFixed(1)})`)
    }/**
     * Update mesh positions based on current layout parameters
     */
    updateMeshPositions() {
        if (this.meshes.length !== 6) return // Only update if we have all meshes
        
        const positions = [
            // Red meshes (left side)
            { 
                x: -this.layoutParams.horizontalSpread, 
                y: this.layoutParams.verticalSpread * 0.5, 
                z: -this.layoutParams.overallDistance 
            },
            { 
                x: -this.layoutParams.horizontalSpread * 1.2, 
                y: -this.layoutParams.verticalSpread * 0.3, 
                z: -this.layoutParams.overallDistance - this.layoutParams.depthVariation 
            },
            
            // Yellow meshes (center)
            { 
                x: 0, 
                y: 0, 
                z: -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 0.5 
            },
            { 
                x: this.layoutParams.horizontalSpread * 0.3, 
                y: -this.layoutParams.verticalSpread * 0.8, 
                z: -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 0.2 
            },
            
            // Blue meshes (right side)
            { 
                x: this.layoutParams.horizontalSpread, 
                y: this.layoutParams.verticalSpread * 0.3, 
                z: -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 0.7 
            },
            { 
                x: this.layoutParams.horizontalSpread * 1.3, 
                y: -this.layoutParams.verticalSpread * 0.5, 
                z: -this.layoutParams.overallDistance - this.layoutParams.depthVariation * 1.5 
            }
        ]
          this.meshes.forEach((mesh, index) => {
            const pos = positions[index]
            // Apply offset to position
            mesh.position.set(
                pos.x + this.layoutParams.horizontalOffset, 
                pos.y + this.layoutParams.verticalOffset, 
                pos.z
            )
            
            // Dynamic scaling: objects further away scale up to maintain apparent size
            const distanceFactor = Math.abs(pos.z) / this.layoutParams.overallDistance
            const scale = this.layoutParams.scale * distanceFactor
            mesh.scale.set(scale, scale, scale)
        })
    }

    /**
     * Update shader uniforms for all materials
     */
    update(elapsedTime) {
        this.materials.forEach(({ material }) => {
            material.uniforms.uTime.value = elapsedTime
        })
    }

    updateResolution(width, height) {
        this.resolution.set(width, height)
        this.materials.forEach(({ material }) => {
            if (material.uniforms.uResolution) {
                material.uniforms.uResolution.value.copy(this.resolution)
            }
        })
    }

    /**
     * Update a specific shader parameter across all materials
     */
    updateShaderParam(paramName, value) {
        const uniformName = `u${paramName.charAt(0).toUpperCase()}${paramName.slice(1)}`
        this.materials.forEach(({ material }) => {
            if (material.uniforms[uniformName]) {
                material.uniforms[uniformName].value = value
            }
        })
    }

    /**
     * Get all materials for external manipulation (e.g., GUI)
     */
    getMaterials() {
        return this.materials.map(m => m.material)
    }

    /**
     * Get all meshes
     */
    getMeshes() {
        return this.meshes
    }

    /**
     * Clean up resources
     */
    dispose() {
        this.meshes.forEach(mesh => {
            if (mesh.geometry) mesh.geometry.dispose()
            if (mesh.material) mesh.material.dispose()
            this.scene.remove(mesh)
        })
        this.meshes = []
        this.materials = []
    }
}

export default HeroScene
